import { ModuleKey, SELF_SERVICE, WidgetDef } from './types';
import { attendanceWidgets } from './attendanceWidgets';
import { leaveWidgets } from './leaveWidgets';
import { peopleWidgets } from './peopleWidgets';
import { accountingWidgets } from './accountingWidgets';
import { inventoryWidgets } from './inventoryWidgets';
import { salesWidgets } from './salesWidgets';
import { payrollWidgets } from './payrollWidgets';
import { expenseWidgets } from './expenseWidgets';
import { maintenanceWidgets } from './maintenanceWidgets';
import { merchandisingWidgets } from './merchandisingWidgets';

export * from './types';

export const WIDGET_REGISTRY: WidgetDef[] = [
    ...attendanceWidgets,
    ...leaveWidgets,
    ...peopleWidgets,
    ...accountingWidgets,
    ...inventoryWidgets,
    ...salesWidgets,
    ...payrollWidgets,
    ...expenseWidgets,
    ...maintenanceWidgets,
    ...merchandisingWidgets,
];

export function getWidget(id: string): WidgetDef | undefined {
    return WIDGET_REGISTRY.find(w => w.id === id);
}

/**
 * Roles that hold a board key INSTEAD of DASHBOARD_VIEW (it also guards the attendance/leave/people
 * endpoints, which they must not read). The server's dashboard endpoints accept these board keys, so the
 * widgets of those modules (and only those) are visible on a board key as well; module read access is
 * still required below. Finance boards (AP/AR clerk, payment approver, auditor): accounting and sales.
 * Accountant: accounting, sales, expense, payroll and inventory.
 */
const FINANCE_BOARD_KEYS = [
    'DASHBOARD_VIEW_PAYABLES', 'DASHBOARD_VIEW_RECEIVABLES', 'DASHBOARD_VIEW_PAYMENT_APPROVAL', 'DASHBOARD_VIEW_AUDIT',
];
const BOARD_KEYS_BY_MODULE: Partial<Record<ModuleKey, string[]>> = {
    accounting: [...FINANCE_BOARD_KEYS, 'DASHBOARD_VIEW_ACCOUNTANT'],
    sales: [...FINANCE_BOARD_KEYS, 'DASHBOARD_VIEW_ACCOUNTANT'],
    expense: ['DASHBOARD_VIEW_ACCOUNTANT'],
    payroll: ['DASHBOARD_VIEW_ACCOUNTANT'],
    inventory: ['DASHBOARD_VIEW_ACCOUNTANT'],
};

function passesBoardKeyGate(widget: WidgetDef, has: (key: string) => boolean): boolean {
    return widget.permission === 'DASHBOARD_VIEW'
        && (BOARD_KEYS_BY_MODULE[widget.module] ?? []).some(k => has(k));
}

/**
 * Whether a widget's permission gate admits the caller's effective permission set. Boards must
 * apply this to PERSISTED layouts too, not just to the Add-widget picker: a layout saved while the
 * user held a since-revoked permission survives the revocation, and rendering those widgets
 * afterwards means a burst of 403s behind permanent spinners.
 */
export function isWidgetVisible(widget: WidgetDef, has: (key: string) => boolean): boolean {
    const gatePassed = widget.permission === SELF_SERVICE
        || has(widget.permission)
        || (widget.alsoVisibleWith ?? []).some(group => group.every(k => has(k)))
        || passesBoardKeyGate(widget, has);
    return gatePassed && hasModuleViewAccess(widget.module, has);
}

/**
 * The module's own read permission(s), required IN ADDITION to the dashboard permission for the
 * money-bearing modules -- the server enforces the same pair, so a role that only holds the
 * dashboard permission must not be shown a board that would answer 403. Any one key of a list is
 * enough. Modules absent here (attendance, leave, people, ...) need nothing beyond the dashboard key.
 */
const MODULE_VIEW_KEYS: Partial<Record<ModuleKey, string[]>> = {
    accounting: ['FINANCIAL_REPORT_VIEW', 'ACCOUNT_VIEW', 'JOURNAL_ENTRY_VIEW'],
    inventory: ['STOCK_VIEW'],
    sales: ['SALES_INVOICE_VIEW', 'SALES_REPORT_VIEW', 'QUOTATION_VIEW', 'CRM_VIEW'],
    payroll: ['PAYROLL_VIEW'],
    expense: ['EXPENSE_CLAIM_VIEW_ALL'],
};

export function hasModuleViewAccess(module: ModuleKey, has: (key: string) => boolean): boolean {
    const keys = MODULE_VIEW_KEYS[module];
    return !keys || keys.some(k => has(k));
}

export function widgetsFor(has: (key: string) => boolean, enabledModules?: ModuleKey[]): WidgetDef[] {
    return WIDGET_REGISTRY.filter(w => (!enabledModules || enabledModules.includes(w.module)) && isWidgetVisible(w, has));
}
